/**
 * File Name          : learning-layout.component.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.1.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_PROJECTTECH_LICENSE.txt', which is part of this source code package.
 */

import { Component, OnInit, Type } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DatasnapDeviceService } from '../../services/datasnap-device.service';
import { DatasnapLesson, DatasnapLevel, DatasnapUnit, UNITS } from '../../models/lessons.model';
import { DatasnapDevice } from '../../models/datasnap-device.model';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { DxButtonModule } from 'devextreme-angular';
import { DataCollectionComponent } from "../../components/data-collection/data-collection.component";
import { confirm } from 'devextreme/ui/dialog';
import { filter } from 'rxjs';
import { EggDropComponent } from '../../components/lessons/egg-drop/egg-drop.component';
import { AirRacerComponent } from '../../components/lessons/air-racer/air-racer.component';
import { ProjectileComponent } from '../../components/lessons/projectile/projectile.component';

@Component({
  selector: 'app-learning-layout',
  standalone: true,
  imports: [CommonModule, DxButtonModule, DataCollectionComponent, NgComponentOutlet],
  templateUrl: './learning-layout.component.html',
  styleUrl: './learning-layout.component.scss'
})
export class LearningLayoutComponent implements OnInit {

  unitId!: string;
  unit!: DatasnapUnit|undefined;
  levelId!: string;
  level: DatasnapLevel |undefined;
  lessonId!: string;
  lesson: DatasnapLesson |undefined;

  devices: DatasnapDevice[] = [];
  lessonComponent: Type<any> | null = null;

  constructor(private activatedRoute: ActivatedRoute, private datasnapDeviceService: DatasnapDeviceService, private router: Router) {}

  ngOnInit() {
      // Subscribe to router events to detect navigation changes
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        this.updateFromRoute();
      });
  
      // Initial load
      this.updateFromRoute();
  }

  private updateFromRoute() {
    // Access the deepest child route to get all params
    let child = this.activatedRoute;
    while (child) {
      if (child.firstChild) {
        child = child.firstChild;
      } else {
        this.unitId = this.activatedRoute.snapshot.paramMap.get('unitId') ?? '';
        this.unit = UNITS.find(l => l.id === this.unitId);
        this.levelId = this.activatedRoute.snapshot.paramMap.get('levelId') ?? '';
        this.level = this.unit?.levels.find(l => l.id === this.levelId);
        this.lessonId = this.activatedRoute.snapshot.paramMap.get('lessonId') ?? '';
        this.lesson = this.level?.lessons.find(l => l.id === this.lessonId);

        if (!this.unit || !this.level) {
          this.router.navigate(['/home']);
          return;
        }

        this.lessonComponent = this.loadLessonComponent(this.unitId);

        this.datasnapDeviceService.devices$.subscribe(devices => {
          this.devices = devices;
          this.devices.forEach(device => { device.unitId = this.unitId; });
        });
        
        break;
      }
    }
  }


  private loadLessonComponent(unitId: string): Type<any> | null {
    switch (unitId) {
      case 'impact':
        return EggDropComponent;
      case 'air_racer':
        return AirRacerComponent;
      case 'projectile':
        return ProjectileComponent;
      default:
        return null;
    }
  }

  lessonInputs(): Record<string, any> {
    return {
      results: this.devices[0]?.getResultsAny() ?? null,
      scalars: this.lesson?.scalars ?? [],
      series: this.lesson?.series ?? []
    };
  }

  addLessonCard = () => {
    this.datasnapDeviceService.addNewDevice(this.unitId);
  }

  removeDevice = (device: DatasnapDevice) => {
    confirm("Are you sure you want to disconnect your device? All progress will be lost", "Disconnect Device").then((dialogResult) => {
      if (dialogResult) {
        this.datasnapDeviceService.removeDevice(device);
      }
    }).catch((error) => {
      console.error("Error showing confirmation dialog:", error);
    });
    
  }

  checkGoHome = () => {
    confirm("Are you sure you want to leave the lesson? All progress will be lost", "Home").then((dialogResult) => {
      if (dialogResult) {
        this.datasnapDeviceService.removeAllDevices();
        this.router.navigate(['home'])
      }
    }).catch((error) => {
      console.error("Error showing confirmation dialog:", error);
    });
  }
}
